import { useRef } from 'react';

/**
 * A slider with a small number of named stops.
 *
 * Built for the vehicle's Speed control, which the incumbent draws as a track
 * with `Half`, `Normal` and `2x` marked along it and the handle resting on one
 * of them. We had a select, which holds the same three values but reads as a
 * different kind of choice: a slider says "this is a dial with a middle", a
 * dropdown says "these are three unrelated options".
 *
 * Not a native `<input type="range">`. That control cannot label its stops,
 * styles differently in every browser, and its thumb cannot be given a focus
 * ring that matches the rest of the console -- the same reasons the other
 * native controls are avoided here. Keyboard support is the part people
 * actually lose when a range input is replaced, so it is implemented outright:
 * arrows step, Home and End jump to the ends.
 */

export interface Stop<T extends string | number> {
  value: T;
  label: string;
}

export function StopSlider<T extends string | number>({
  value,
  stops,
  onChange,
  label,
  leadingIcon,
  trailingIcon,
}: {
  value: T;
  stops: Stop<T>[];
  onChange: (value: T) => void;
  label: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const index = Math.max(
    0,
    stops.findIndex((s) => s.value === value),
  );
  const last = stops.length - 1;
  const pct = last > 0 ? (index / last) * 100 : 0;

  const move = (next: number) => {
    const clamped = Math.min(last, Math.max(0, next));
    const stop = stops[clamped];
    if (stop && stop.value !== value) onChange(stop.value);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const keys: Record<string, number> = {
      ArrowLeft: index - 1,
      ArrowDown: index - 1,
      ArrowRight: index + 1,
      ArrowUp: index + 1,
      Home: 0,
      End: last,
    };
    const next = keys[event.key];
    if (next === undefined) return;
    event.preventDefault();
    move(next);
  };

  /* Pointer anywhere on the track jumps to the nearest stop, as a slider does. */
  const onPointerDown = (event: React.PointerEvent) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    const ratio = (event.clientX - box.left) / box.width;
    move(Math.round(ratio * last));
  };

  return (
    <div className="stop-slider">
      {leadingIcon ? <span className="stop-slider-icon">{leadingIcon}</span> : null}

      <div className="stop-slider-body">
        <div
          ref={ref}
          className="stop-slider-track"
          role="slider"
          tabIndex={0}
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={last}
          aria-valuenow={index}
          aria-valuetext={stops[index]?.label ?? ''}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
        >
          <span className="stop-slider-fill" style={{ width: `${pct}%` }} />
          {stops.map((s, i) => (
            <span
              key={String(s.value)}
              className="stop-slider-tick"
              style={{ left: `${last > 0 ? (i / last) * 100 : 0}%` }}
              data-passed={i <= index ? 'true' : undefined}
            />
          ))}
          <span className="stop-slider-thumb" style={{ left: `${pct}%` }} />
        </div>

        <div className="stop-slider-labels">
          {stops.map((s, i) => (
            <button
              key={String(s.value)}
              type="button"
              className="stop-slider-label"
              data-current={i === index ? 'true' : undefined}
              style={{ left: `${last > 0 ? (i / last) * 100 : 0}%` }}
              onClick={() => move(i)}
              tabIndex={-1}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {trailingIcon ? <span className="stop-slider-icon">{trailingIcon}</span> : null}
    </div>
  );
}
